import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';
import Loading from '../../components/ui/Loading';
import { useAPI } from '../../hooks/useAPI';
import { getActivityDetail } from '../../services/api/activity';
import { formatDate } from '../../utils/formatters';
import { processRichTextImages } from '../../services/api/index.js';

/**
 * ActivityDetail - 活动详情页面
 * 展示单个活动的完整信息
 */
const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // 使用 useAPI Hook 获取活动详情
  const { data: activity, loading, error } = useAPI(
    () => getActivityDetail(id, i18n.language),
    [id, i18n.language]
  );

  // 格式化活动时间
  const formatActivityTime = (startDate, endDate) => {
    if (!startDate) return '';

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    if (end && start.toDateString() !== end.toDateString()) {
      return `${formatDate(start, 'YYYY 年 MM 月 DD 日')} - ${formatDate(end, 'YYYY 年 MM 月 DD 日')}`;
    } else {
      return `${formatDate(start, 'YYYY 年 MM 月 DD 日')} ${formatDate(start, 'HH:mm')}`;
    }
  };

  // 渲染加载状态
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  // 渲染错误状态
  if (error || !activity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('common.error.notFound', '页面未找到')}
          </h2>
          <p className="text-gray-600 mb-6">
            {error || t('activities.error.notFound', '活动不存在或已被删除')}
          </p>
          <button
            onClick={() => navigate('/activities')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            {t('common.backToList', '返回列表')}
          </button>
        </div>
      </div>
    );
  }

  const {
    title,
    description,
    category,
    startDate,
    endDate,
    location,
    organizer,
    coOrganizers = [],
    coverImage,
    images = [],
    tags = [],
    content,
    agenda = [],
    speakers = [],
    registrationInfo,
    contactInfo
  } = activity;

  // 处理富文本内容中的图片路径
  const processedContent = processRichTextImages(content);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 活动基本信息 */}
      <section className="bg-white">
        <div className="container mx-auto px-4 py-8">
          {/* 标题部分 - 跨整个宽度 */}
          <div className="mb-8">
            {/* 返回按钮 */}
            <button
              onClick={() => navigate('/activities')}
              className="flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('common.backToList', '返回列表')}
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                {category}
              </span>
              <span className="text-sm text-gray-500">
                {formatDate(new Date(), 'YYYY-MM-DD')}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h1>

            {description && (
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {description}
              </p>
            )}
          </div>

          {/* 网格布局 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 主要信息 */}
            <div className="lg:col-span-2 space-y-6">
              {/* 基本信息 - 只有在有地点或主办方时才显示 */}
              {(location || organizer) && (
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 h-20">
                  <div className="h-full flex items-center justify-between flex-wrap gap-2">
                    {/* 地点 */}
                    {location && (
                      <div className="flex items-center text-xs">
                        <MapPin className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="font-medium text-gray-900">{t('activities.details.location', '活动地点')}：</span>
                        <span className="text-gray-600">{location}</span>
                      </div>
                    )}

                    {/* 主办方 */}
                    {organizer && (
                      <div className="flex items-center text-xs">
                        <Users className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="font-medium text-gray-900">{t('activities.details.organizer', '主办方')}：</span>
                        <span className="text-gray-600">{organizer}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex flex-wrap gap-3">
                {registrationInfo?.isRequired && (
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
                    onClick={() => {
                      if (registrationInfo.registrationUrl) {
                        window.open(registrationInfo.registrationUrl, '_blank');
                      } else {
                        // TODO: 实现报名功能
                        console.log('报名活动:', id);
                      }
                    }}
                  >
                    {t('activities.actions.register', '立即报名')}
                  </button>
                )}
              </div>
            </div>

            {/* 侧栏信息 */}
            <div className="space-y-6">
              {/* 活动封面 */}
              {coverImage && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <img
                    src={coverImage}
                    alt={title}
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}

              {/* 联系方式 */}
              {contactInfo && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {t('activities.details.contact', '联系方式')}
                  </h3>
                  <div className="space-y-3">
                    {contactInfo.email && (
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-gray-400 mr-3" />
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    )}

                    {contactInfo.phone && (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-gray-400 mr-3" />
                        <a
                          href={`tel:${contactInfo.phone}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    )}

                    {contactInfo.website && (
                      <div className="flex items-center">
                        <ExternalLink className="w-4 h-4 text-gray-400 mr-3" />
                        <a
                          href={contactInfo.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {t('activities.details.website', '官方网站')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 标签 */}
              {tags.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {t('common.tags', '标签')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 详细内容 */}
      <section className="bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 py-8">
          {/* 标签页内容 */}
          <div className="prose max-w-none mx-auto">
            <div>
              {processedContent ? (
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            ) : (
                <p className="text-gray-500">
                  {t('activities.noContent', '暂无详细介绍')}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ActivityDetail;
